import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import SubscriptionCard from '../components/SubscriptionCard'
import ManageTags from '../components/ManageTags'
import ManagePaymentMethods from '../components/ManagePaymentMethods'
import ManageVehicles from '../components/ManageVehicles'
import ManageCategories from '../components/ManageCategories'

const teal = '#0D9488'
const plum = '#1E1040'

export default function Settings() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [profile, setProfile] = useState(null)

  // Profile edit state
  const [fullName, setFullName] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileMsg, setProfileMsg] = useState('')

  // Mileage rate edit state (stored in cents on profile)
  const [rateDollars, setRateDollars] = useState('')
  const [savingRate, setSavingRate] = useState(false)
  const [rateMsg, setRateMsg] = useState('')

  // Which section is expanded (accordion-style for the heavier ones)
  const [expanded, setExpanded] = useState({
    subscription: true,
    tags: false,
    payments: false,
    vehicles: false,
    categories: false,
  })

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    setEmail(user.email || '')

    const { data: profileRow } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profileRow) {
      setProfile(profileRow)
      setFullName(profileRow.full_name || '')
      const cents = profileRow.mileage_rate_cents
      setRateDollars(cents != null ? (cents / 100).toFixed(2) : '0.70')
    }
    setLoading(false)
  }

  const saveProfile = async () => {
    setSavingProfile(true)
    setProfileMsg('')
    const { data: { user } } = await supabase.auth.getUser()
    const { error: err } = await supabase
      .from('profiles')
      .update({ full_name: fullName.trim() })
      .eq('id', user.id)

    if (err) {
      setProfileMsg(t('settings.error_prefix', { msg: err.message }))
    } else {
      setProfileMsg(t('settings.saved'))
      setTimeout(() => setProfileMsg(''), 2000)
    }
    setSavingProfile(false)
  }

  const saveRate = async () => {
    setSavingRate(true)
    setRateMsg('')
    const parsed = parseFloat(rateDollars)
    if (isNaN(parsed) || parsed < 0 || parsed > 5) {
      setRateMsg(t('settings.error_rate_range'))
      setSavingRate(false)
      return
    }
    const cents = Math.round(parsed * 100)
    const { data: { user } } = await supabase.auth.getUser()
    const { error: err } = await supabase
      .from('profiles')
      .update({ mileage_rate_cents: cents })
      .eq('id', user.id)

    if (err) {
      setRateMsg(t('settings.error_prefix', { msg: err.message }))
    } else {
      setRateMsg(t('settings.saved'))
      setTimeout(() => setRateMsg(''), 2000)
    }
    setSavingRate(false)
  }

  const handleSignOut = async () => {
    if (!window.confirm(t('settings.signout_confirm'))) return
    await supabase.auth.signOut()
    navigate('/login')
  }

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang)
    localStorage.setItem('i18nextLng', newLang)
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>{t('settings.loading')}</div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t('settings.title')}</h1>
        <p style={styles.subtitle}>
          {t('settings.subtitle')}
        </p>
      </div>

      {/* LANGUAGE */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>{t('settings.language_title')}</h2>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.langGrid}>
            <button
              style={{
                ...styles.langBtn,
                ...(i18n.language === 'en' ? styles.langBtnActive : {}),
              }}
              onClick={() => handleLanguageChange('en')}
            >
              <span style={styles.langFlag}>🇺🇸</span>
              <span style={styles.langName}>English</span>
              {i18n.language === 'en' && <span style={styles.langCheck}>✓</span>}
            </button>
            <button
              style={{
                ...styles.langBtn,
                ...(i18n.language === 'es' ? styles.langBtnActive : {}),
              }}
              onClick={() => handleLanguageChange('es')}
            >
              <span style={styles.langFlag}>🌎</span>
              <span style={styles.langName}>Español</span>
              {i18n.language === 'es' && <span style={styles.langCheck}>✓</span>}
            </button>
          </div>
          <div style={styles.hint}>{t('settings.language_hint')}</div>
        </div>
      </div>

      {/* PROFILE */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>{t('settings.profile_title')}</h2>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.field}>
            <label style={styles.label}>{t('settings.name_label')}</label>
            <input
              type="text"
              style={styles.input}
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder={t('settings.name_placeholder')}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>{t('settings.email_label')}</label>
            <input type="text" style={{ ...styles.input, background: '#F9FAFB', color: '#6B7280' }} value={email} disabled />
            <div style={styles.hint}>{t('settings.email_hint')}</div>
          </div>
          <div style={styles.actionsRow}>
            <button
              style={styles.btnPrimary}
              onClick={saveProfile}
              disabled={savingProfile || fullName === (profile?.full_name || '')}
            >
              {savingProfile ? t('common.saving') : t('settings.save_changes')}
            </button>
            {profileMsg && <span style={styles.msg}>{profileMsg}</span>}
          </div>
        </div>
      </div>

      {/* MILEAGE RATE */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>{t('settings.mileage_rate_title')}</h2>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.field}>
            <label style={styles.label}>{t('settings.rate_label')}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="5"
              style={{ ...styles.input, maxWidth: '180px' }}
              value={rateDollars}
              onChange={e => setRateDollars(e.target.value)}
            />
            <div style={styles.hint}>{t('settings.rate_hint')}</div>
          </div>
          <div style={styles.actionsRow}>
            <button
              style={styles.btnPrimary}
              onClick={saveRate}
              disabled={savingRate}
            >
              {savingRate ? t('common.saving') : t('settings.save_rate')}
            </button>
            {rateMsg && <span style={styles.msg}>{rateMsg}</span>}
          </div>
        </div>
      </div>

      {/* SUBSCRIPTION */}
      <div style={styles.card}>
        <button style={styles.cardHeaderBtn} onClick={() => toggle('subscription')}>
          <h2 style={styles.cardTitle}>{t('settings.subscription_title')}</h2>
          <span style={styles.chevron}>{expanded.subscription ? '▾' : '▸'}</span>
        </button>
        {expanded.subscription && (
          <div style={styles.cardBody}>
            <SubscriptionCard />
          </div>
        )}
      </div>

      {/* TAGS */}
      <div style={styles.card}>
        <button style={styles.cardHeaderBtn} onClick={() => toggle('tags')}>
          <h2 style={styles.cardTitle}>{t('settings.tags_title')}</h2>
          <span style={styles.chevron}>{expanded.tags ? '▾' : '▸'}</span>
        </button>
        {expanded.tags && (
          <div style={styles.cardBody}>
            <ManageTags />
          </div>
        )}
      </div>

      {/* PAYMENT METHODS */}
      <div style={styles.card}>
        <button style={styles.cardHeaderBtn} onClick={() => toggle('payments')}>
          <h2 style={styles.cardTitle}>{t('settings.payments_title')}</h2>
          <span style={styles.chevron}>{expanded.payments ? '▾' : '▸'}</span>
        </button>
        {expanded.payments && (
          <div style={styles.cardBody}>
            <ManagePaymentMethods />
          </div>
        )}
      </div>

      {/* VEHICLES */}
      <div style={styles.card}>
        <button style={styles.cardHeaderBtn} onClick={() => toggle('vehicles')}>
          <h2 style={styles.cardTitle}>{t('settings.vehicles_title')}</h2>
          <span style={styles.chevron}>{expanded.vehicles ? '▾' : '▸'}</span>
        </button>
        {expanded.vehicles && (
          <div style={styles.cardBody}>
            <ManageVehicles />
          </div>
        )}
      </div>

      {/* CATEGORIES */}
      <div style={styles.card}>
        <button style={styles.cardHeaderBtn} onClick={() => toggle('categories')}>
          <h2 style={styles.cardTitle}>{t('settings.categories_title')}</h2>
          <span style={styles.chevron}>{expanded.categories ? '▾' : '▸'}</span>
        </button>
        {expanded.categories && (
          <div style={styles.cardBody}>
            <ManageCategories />
          </div>
        )}
      </div>

      {/* SIGN OUT */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>{t('settings.signout_title')}</h2>
        </div>
        <div style={styles.cardBody}>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 12px 0', fontWeight: 500 }}>
            {t('settings.signout_body')}
          </p>
          <button style={styles.btnDanger} onClick={handleSignOut}>
            {t('settings.signout_btn')}
          </button>
        </div>
      </div>

      <div style={styles.footer}>
        Merq Log · merqlog.com
      </div>
    </div>
  )
}

const styles = {
  page: {
    padding: '32px 40px 80px',
    maxWidth: '880px',
    margin: '0 auto',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum,
  },
  loading: { padding: '60px', textAlign: 'center', color: '#9CA3AF' },
  header: { marginBottom: '28px' },
  title: {
    fontFamily: "'Fraunces', serif",
    fontSize: '36px', fontWeight: 900,
    letterSpacing: '-1px', color: plum, margin: 0, lineHeight: 1.1,
  },
  subtitle: {
    fontSize: '15px', color: '#6B7280',
    marginTop: '6px', fontWeight: 500,
  },
  card: {
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '16px',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '18px 22px',
    borderBottom: '1px solid #F3F4F6',
  },
  cardHeaderBtn: {
    padding: '18px 22px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', background: '#fff', border: 'none',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  cardTitle: {
    fontFamily: "'Fraunces', serif", fontSize: '18px',
    fontWeight: 800, color: plum, margin: 0, letterSpacing: '-0.2px',
  },
  chevron: {
    fontSize: '14px', color: '#9CA3AF', fontWeight: 700,
  },
  cardBody: { padding: '22px' },
  field: { marginBottom: '14px' },
  label: {
    display: 'block', fontSize: '11px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  hint: {
    fontSize: '12px', color: '#9CA3AF',
    marginTop: '6px', fontWeight: 500, lineHeight: 1.4,
  },
  actionsRow: {
    display: 'flex', gap: '12px', alignItems: 'center',
    marginTop: '8px',
  },
  msg: {
    fontSize: '13px', color: teal, fontWeight: 600,
  },
  btnPrimary: {
    padding: '10px 20px', background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px',
    fontSize: '13px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  btnDanger: {
    padding: '10px 20px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: '100px',
    fontSize: '13px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  footer: {
    marginTop: '32px', textAlign: 'center',
    fontSize: '12px', color: '#9CA3AF', fontWeight: 500,
  },
  // Language switcher
  langGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
    marginBottom: '8px',
  },
  langBtn: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', background: '#fff',
    border: '1.5px solid #E5E7EB', borderRadius: '12px',
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'all 0.15s',
  },
  langBtnActive: {
    background: 'rgba(13, 148, 136, 0.06)',
    borderColor: teal, borderWidth: '2px',
  },
  langFlag: { fontSize: '22px' },
  langName: { fontSize: '14px', fontWeight: 700, color: plum, flex: 1, textAlign: 'left' },
  langCheck: { color: teal, fontSize: '16px', fontWeight: 800 },
}
